/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useContext, useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  SuggestedItems,
  SuggestedItem,
} from '@bingads-webui-campaign-react/pill-item-components';
import { useTheme } from 'react-jss';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Stack } from '@fluentui/react';
import { DefaultButton } from '@bingads-webui-react/fabric-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SparkleImgSVGIcon, SparkleImgIcon } from '@bingads-webui-campaign-react/sparkle-icon';
import {
  TextTitle,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { SectionShimmer } from './section-shimmer';
import { useCircularBufferView } from './util/use-circular-buffer-view';
import { getkeywordSuggestionStyles, getAddAllButtonStyle, getExploreKeywordsButtonStyle } from './style/keyword-suggestion-style';

export const MAX_INLINE_RECOMMENDATIONS_PER_PAGE = 13;

export const KeywordSuggestion = (props) => {
  const activity = useContext(ActivityContext);
  const classes = useDefaultStyles(getkeywordSuggestionStyles);
  const theme = useTheme();
  const { components, core } = theme;
  const i18n = useContext(I18nContext);
  const {
    renderSuggestedItem,
    onAddSuggestedItem,
    renderMoreSuggestionsInlineButton,
    onAddAllSuggestedItems,
    onMoreSuggestionsButtonClick,
    disableAllSuggestions,
    disabledSuggestionTooltip,
    useStyleFluent2,
    suggestedItems,
    showSuggestionsSectionShimmer,
    perfMarker,
  } = props;

  const [availableSuggestedItems, canMoveNext, moveNext] = useCircularBufferView(suggestedItems, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
  const isAvailableSuggestedItemsEmpty = _.isEmpty(availableSuggestedItems);
  const suggestedItemsFooter = useMemo(() => (
    <>
      <Stack horizontal wrap tokens={{ childrenGap: 8, padding: '24px 0 4px 0' }}>
        <SuggestedItem
          itemText={i18n.getString(_TL_('Add all'))}
          itemIndex={0}
          onAdd={() => onAddAllSuggestedItems(availableSuggestedItems, activity)}
          useStyleFluent2={useStyleFluent2}
          useCustomStyle
          getCustomStyle={getAddAllButtonStyle}
        />
        <DefaultButton
          className={classNames('suggested-item-add')}
          styles={{ ...getExploreKeywordsButtonStyle(components, core) }}
          onClick={() => onMoreSuggestionsButtonClick(activity)}
          iconProps={{ iconName: SparkleImgSVGIcon }}
          title={i18n.getString(_TL_('Explore keywords'))}
          name="Explore keywords"
          allowDisabledFocus
        >
          {i18n.getString(_TL_('Explore keywords'))}
        </DefaultButton>
      </Stack>
    </>
  ), [
    onAddAllSuggestedItems,
    onMoreSuggestionsButtonClick,
    i18n,
    useStyleFluent2,
    components,
    core,
    availableSuggestedItems,
    activity,
  ]);

  const emptySuggestedItemsView = useMemo(() => (
    <>
      <Stack horizontal wrap tokens={{ childrenGap: 8, padding: '0 0 0 0' }}>
        <TextTitle titleKey={_TL_('Looking for keyword suggestions?')} icon={SparkleImgIcon} />
        <DefaultButton
          className={classNames('suggested-item-add')}
          styles={{ ...getExploreKeywordsButtonStyle(components, core, isAvailableSuggestedItemsEmpty) }}
          onClick={() => onMoreSuggestionsButtonClick(activity)}
          title={i18n.getString(_TL_('Explore keywords'))}
          name="Explore keywords"
          allowDisabledFocus
        >
          {i18n.getString(_TL_('Explore keywords'))}
        </DefaultButton>
      </Stack>
    </>
  ), [
    onMoreSuggestionsButtonClick,
    i18n,
    components,
    core,
    isAvailableSuggestedItemsEmpty,
    activity,
  ]);

  const sectionContent = useMemo(() => (
    !isAvailableSuggestedItemsEmpty ?
      <SuggestedItems
        suggestedItems={availableSuggestedItems}
        noSuggestionsDescription=""
        suggestedSectionDescription=""
        renderSuggestedItem={renderSuggestedItem}
        onAddSuggestedItem={_.partial(onAddSuggestedItem, _, _, activity)}
        showMoreSuggestionsInlineButton={canMoveNext}
        renderMoreSuggestionsInlineButton={renderMoreSuggestionsInlineButton}
        onMoreSuggestionsInlineButtonClick={moveNext}
        suggestedItemsFooter={suggestedItemsFooter}
        disableAllSuggestions={disableAllSuggestions}
        disabledSuggestionTooltip={disabledSuggestionTooltip}
        useStyleFluent2={useStyleFluent2}
      /> :
      <>
        {emptySuggestedItemsView && emptySuggestedItemsView}
      </>
  ), [
    availableSuggestedItems,
    canMoveNext,
    moveNext,
    renderSuggestedItem,
    onAddSuggestedItem,
    renderMoreSuggestionsInlineButton,
    suggestedItemsFooter,
    disableAllSuggestions,
    disabledSuggestionTooltip,
    useStyleFluent2,
    emptySuggestedItemsView,
    isAvailableSuggestedItemsEmpty,
    activity,
  ]);

  useState(() => {
    perfMarker.willInit();
  });

  useEffect(() => {
    if (!showSuggestionsSectionShimmer) {
      perfMarker.done();
    }
  }, [showSuggestionsSectionShimmer, perfMarker]);

  return (
    <div className={classes.keywordSuggestionContainer}>
      {showSuggestionsSectionShimmer ?
        <SectionShimmer /> :
        <>
          {sectionContent && sectionContent}
        </>
      }
    </div>
  );
};

KeywordSuggestion.propTypes = {
  suggestedItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showSuggestionsSectionShimmer: PropTypes.bool.isRequired,
  onAddSuggestedItem: PropTypes.func.isRequired,
  renderSuggestedItem: PropTypes.func,
  renderMoreSuggestionsInlineButton: PropTypes.func,
  onAddAllSuggestedItems: PropTypes.func,
  onMoreSuggestionsButtonClick: PropTypes.func,
  disableAllSuggestions: PropTypes.bool,
  disabledSuggestionTooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  useStyleFluent2: PropTypes.bool,
  perfMarker: PropTypes.shape({
    willInit: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
  }).isRequired,
};

KeywordSuggestion.defaultProps = {
  renderSuggestedItem: SuggestedItems.defaultProps.renderSuggestedItem,
  renderMoreSuggestionsInlineButton: SuggestedItems.defaultProps.renderMoreSuggestionsInlineButton,
  onAddAllSuggestedItems: _.noop,
  onMoreSuggestionsButtonClick: _.noop,
  disableAllSuggestions: false,
  disabledSuggestionTooltip: undefined,
  useStyleFluent2: true,
};
